import { useEffect, useRef, useState } from 'react';
import { useLocale, useT, withLocale } from '../../i18n';
import { lisaApi, voiceApi, type RecommendationCard } from '../../api/lisa';
import { ApiRequestError } from '../../api/client';
import { useLisaAvatar3D } from './useLisaAvatar3D';
import { useLisaPageContext } from './PageContext';

type Status = 'idle' | 'listening' | 'thinking' | 'speaking';
type TranscriptTurn = { sender: 'visitor' | 'lisa'; text: string; cards?: RecommendationCard[] };

const MUTE_KEY = 'cyrkil_lisa_muted';

export default function LisaWidget() {
  const locale = useLocale();
  const t = useT();
  const pageCtx = useLisaPageContext();

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionState, setSessionState] = useState('onboarding');
  const [status, setStatus] = useState<Status>('idle');
  const [text, setText] = useState('');
  const [cards, setCards] = useState<RecommendationCard[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [transcript, setTranscript] = useState<TranscriptTurn[]>([]);
  const [transcriptVisible, setTranscriptVisible] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [consentToStore, setConsentToStore] = useState(false);
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem(MUTE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [listening, setListening] = useState(false);
  const [micSupported] = useState(() => typeof window !== 'undefined' && !!(window.SpeechRecognition || (window as any).webkitSpeechRecognition));
  const [voiceConfig, setVoiceConfig] = useState<{ stt_provider: string; tts_provider: string } | null>(null);
  const [humanCtaHref, setHumanCtaHref] = useState(withLocale('/contact', locale) + '?purpose=start_transformation');
  const [errorBanner, setErrorBanner] = useState(false);

  const portraitWrapRef = useRef<HTMLDivElement>(null);
  const portrait3DRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sessionIdRef = useRef<string | null>(null);
  sessionIdRef.current = sessionId;

  const avatar3D = useLisaAvatar3D(portrait3DRef);

  useEffect(() => {
    voiceApi.config().then(setVoiceConfig).catch(() => setVoiceConfig({ stt_provider: 'browser', tts_provider: 'browser' }));
  }, []);

  useEffect(() => {
    avatar3D.setState(status);
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Any element on any page can open LISA via data-open-lisa (the hero's
  // "Ask LISA." hint, solution-page CTAs, etc.) — mirrors the original
  // static build's site.js document-level delegated listener.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if ((e.target as HTMLElement)?.closest?.('[data-open-lisa]')) {
        e.preventDefault();
        openPanel();
      }
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function speak(spoken: string) {
    if (muted) return;
    if (voiceConfig?.tts_provider === 'browser' || !voiceConfig) {
      if (!('speechSynthesis' in window)) return;
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(spoken);
      utter.lang = { en: 'en-US', fr: 'fr-FR', ar: 'ar-SA' }[locale];
      utter.rate = 1.02;
      utter.pitch = 1.05;
      utter.onstart = () => setStatus('speaking');
      utter.onend = () => setStatus('idle');
      utter.onerror = () => setStatus('idle');
      window.speechSynthesis.speak(utter);
    } else {
      setStatus('speaking');
      fetch('/api/voice/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: spoken, locale }),
      })
        .then((res) => (res.ok ? res.blob() : Promise.reject(new Error('tts failed'))))
        .then((blob) => {
          const audio = new Audio(URL.createObjectURL(blob));
          audio.onended = () => setStatus('idle');
          audio.onerror = () => setStatus('idle');
          void audio.play();
        })
        .catch(() => setStatus('idle'));
    }
  }

  async function ensureSession(): Promise<string> {
    if (sessionIdRef.current) return sessionIdRef.current;
    const res = await lisaApi.createSession({
      locale,
      entry_point: pageCtx.entryPoint as any,
      page_context: { path: window.location.pathname, ...(pageCtx.contentId ? { content_id: pageCtx.contentId } : {}) },
      consent: { analytics: false, conversation_storage: consentToStore },
    });
    setSessionId(res.session_id);
    setSessionState(res.state);
    setText(res.opening_message.text);
    setSuggestions(res.opening_message.quick_replies);
    speak(res.opening_message.text);
    return res.session_id;
  }

  async function doHandover(reason: string) {
    const sid = sessionIdRef.current;
    if (!sid) return;
    setStatus('thinking');
    try {
      const res = await lisaApi.handover({ session_id: sid, reason: reason as any, consent_to_share_summary: true });
      setSessionState('handover');
      setText(t('lisa.human_expert_cta') + ` — ${res.expected_response_window}.`);
      setCards([]);
      setSuggestions([]);
      setHumanCtaHref(withLocale('/contact', locale) + `?purpose=technical_discussion`);
      setTranscript((prev) => [...prev, { sender: 'lisa', text: `${res.summary}` }]);
      setStatus('idle');
    } catch {
      setErrorBanner(true);
      setStatus('idle');
    }
  }

  async function ask(raw: string) {
    const value = raw.trim();
    if (!value) return;
    if (value === t('lisa.human_link') || value === 'Talk to a human') {
      await doHandover('visitor_requested');
      return;
    }
    setInputValue('');
    setStatus('thinking');
    setErrorBanner(false);
    setTranscript((prev) => [...prev, { sender: 'visitor', text: value }]);

    try {
      const sid = await ensureSession();
      const res = await lisaApi.sendMessage({
        session_id: sid,
        message: { type: 'text', value },
        page_context: { path: window.location.pathname, ...(pageCtx.contentId ? { content_id: pageCtx.contentId } : {}) },
      });
      setSessionState(res.state);
      setText(res.reply.text);
      setCards(res.reply.recommendation_cards);
      setSuggestions(res.reply.quick_replies);
      setTranscript((prev) => [...prev, { sender: 'lisa', text: res.reply.text, cards: res.reply.recommendation_cards }]);
      speak(res.reply.text);
      if (res.state === 'handover' && res.reply.quick_replies.length === 0) {
        // A guardrail-triggered handover with no explicit "talk to a
        // human" quick-reply still needs a visible path forward.
        setSuggestions(['Talk to a human']);
      }
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? "I'm having trouble reaching my knowledge base right now. You can try again, or talk to a person instead."
          : 'Connection issue — please try again in a moment.';
      setText(message);
      setSuggestions(['Talk to a human']);
      setErrorBanner(true);
      setStatus('idle');
    }
  }

  function openPanel() {
    setOpen(true);
    // LISA now uses a fixed portrait image (see lisa-avatar.png) rather
    // than the procedural WebGL avatar, so the 3D avatar is intentionally
    // never mounted here anymore.
    if (!sessionIdRef.current) {
      void ensureSession();
    }
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function closePanel() {
    setOpen(false);
    stopListening();
    window.speechSynthesis?.cancel();
  }

  function toggleMute() {
    setMuted((m) => {
      const next = !m;
      try {
        localStorage.setItem(MUTE_KEY, next ? '1' : '0');
      } catch {
        /* private browsing — mute preference just won't persist */
      }
      if (next) window.speechSynthesis?.cancel();
      return next;
    });
  }

  function startListening() {
    if (voiceConfig?.stt_provider !== 'browser') {
      // A real server-side STT provider is active — recording+upload
      // flow would go here; browser SpeechRecognition is the only path
      // wired in this pass (see IMPLEMENTATION_CHECKLIST.md).
    }
    const Recognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!Recognition || listening) return;
    const recognition = new Recognition();
    recognition.lang = { en: 'en-US', fr: 'fr-FR', ar: 'ar-SA' }[locale];
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onresult = (e: any) => {
      let value = '';
      for (let i = e.resultIndex; i < e.results.length; i++) value += e.results[i][0].transcript;
      setInputValue(value);
      if (e.results[e.results.length - 1].isFinal) {
        stopListening();
        void ask(value);
      }
    };
    recognition.onerror = () => stopListening();
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    setListening(true);
    setStatus('listening');
    try {
      recognition.start();
    } catch {
      /* already started — ignore */
    }
  }

  function stopListening() {
    setListening(false);
    if (status === 'listening') setStatus('idle');
    try {
      recognitionRef.current?.stop();
    } catch {
      /* not started — ignore */
    }
  }

  return (
    <>
      <div className="lisa-disclosure" id="lisaDisclosure">
        {t('lisa.disclosure_pill')}
      </div>
      <button className="lisa-fab" id="lisaFab" aria-haspopup="dialog" aria-expanded={open} aria-controls="lisaPanel" aria-label={t('lisa.fab_aria')} onClick={() => (open ? closePanel() : openPanel())}>
        <img src="/assets/img/lisa-avatar.png" alt="" className="lisa-fab-avatar" />
        <span className="dot" aria-hidden="true" />
      </button>
      <div className={`lisa-scrim ${open ? 'is-open' : ''}`} id="lisaScrim" hidden={!open} onClick={closePanel} />
      <section className={`lisa-panel ${open ? 'is-open' : ''} ${textMode ? 'lisa-text-mode' : ''}`} id="lisaPanel" role="dialog" aria-modal="false" aria-label={t('lisa.fab_aria')}>
        <div className="lisa-panel-head">
          <div className="lisa-panel-title">
            <strong>
              {t('lisa.name')} <span aria-hidden="true">—</span> {t('lisa.subtitle')}
            </strong>
            <span className="lisa-ai-badge">
              <span aria-hidden="true">⚙</span> {t('lisa.ai_badge')}
            </span>
          </div>
          <button className="lisa-close" id="lisaClose" aria-label={t('lisa.close_aria')} onClick={closePanel}>
            ✕
          </button>
        </div>
        <div className="lisa-panel-body">
          <div className="lisa-left">
            <span className={`lisa-status-pill ${status !== 'idle' ? `is-${status}` : ''}`} id="lisaStatus">
              {t(`lisa.status_${status}`)}
            </span>
            <div className={`lisa-portrait-wrap ${status === 'speaking' ? 'is-speaking' : ''} ${status === 'listening' ? 'is-listening' : ''}`} id="lisaPortraitWrap" ref={portraitWrapRef}>
              <div className="lisa-portrait-3d" id="lisaPortrait3D" ref={portrait3DRef} aria-hidden="true" hidden />
              <img src="/assets/img/lisa-avatar.png" alt="" className="lisa-portrait lisa-portrait-fallback" id="lisaPortraitFallback" />
            </div>
            <div className="lisa-answer-box" id="lisaAnswerBox">
              <div className="lisa-text" id="lisaText" aria-live="polite">
                {status === 'thinking' ? (
                  <div className="lisa-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                ) : (
                  text
                )}
              </div>
              <div className="lisa-card" id="lisaCard">
                {cards.map((c) => (
                  <div className="lisa-rec-card" key={c.content_id}>
                    <span className="badge badge-core" style={{ marginBottom: 8 }}>
                      {c.content_id}
                    </span>
                    <h4>{c.title}</h4>
                    <p>{c.reason}</p>
                    <a className="btn btn-secondary btn-sm" href={c.cta.href}>
                      {c.cta.label}
                    </a>
                  </div>
                ))}
              </div>
              {errorBanner && (
                <p className="body-sm" style={{ color: 'var(--status-critical)', marginTop: 8 }}>
                  <button type="button" className="lisa-util-link" onClick={() => ask(transcript.filter((m) => m.sender === 'visitor').slice(-1)[0]?.text ?? '')}>
                    Retry
                  </button>
                </p>
              )}
            </div>
          </div>
          <div className="lisa-right">
            <div className="lisa-right-scroll">
              <div className="lisa-chip-row" id="lisaSuggestions">
                {suggestions.map((s) => (
                  <button key={s} type="button" className="lisa-chip" onClick={() => ask(s)}>
                    {s}
                  </button>
                ))}
              </div>
              <form
                className="lisa-input-row"
                id="lisaForm"
                onSubmit={(e) => {
                  e.preventDefault();
                  void ask(inputValue);
                }}
              >
                <input
                  type="text"
                  id="lisaInput"
                  ref={inputRef}
                  placeholder={t('lisa.input_placeholder')}
                  aria-label={t('lisa.input_aria')}
                  autoComplete="off"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
                <button type="button" className={`lisa-mic-btn ${listening ? 'is-listening' : ''}`} id="lisaMicBtn" aria-label={listening ? t('lisa.mic_aria_stop') : t('lisa.mic_aria')} hidden={!micSupported} onClick={() => (listening ? stopListening() : startListening())}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z" stroke="currentColor" strokeWidth="2" />
                    <path d="M19 11a7 7 0 0 1-14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
                <button type="submit" className="btn btn-primary lisa-ask-btn" aria-label={t('lisa.send_aria')}>
                  {t('lisa.ask')}
                </button>
              </form>
              <p className="lisa-voice-note" id="lisaVoiceNote" hidden={micSupported}>
                {t('lisa.voice_unsupported')}
              </p>
              {!sessionId && (
                <label className="body-sm" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 4 }}>
                  <input type="checkbox" checked={consentToStore} onChange={(e) => setConsentToStore(e.target.checked)} style={{ marginTop: 3 }} />
                  <span>Save this conversation so a human doesn't have to ask me to repeat it, if I hand off.</span>
                </label>
              )}
              <div className="lisa-util-row">
                <button type="button" id="lisaTranscriptToggle" className="lisa-util-link" onClick={() => setTranscriptVisible((v) => !v)}>
                  {transcriptVisible ? t('lisa.hide_transcript') : t('lisa.view_transcript')}
                </button>
                <button type="button" id="lisaModeToggle" className="lisa-util-link" onClick={() => setTextMode((v) => !v)}>
                  {textMode ? t('lisa.switch_voice_mode') : t('lisa.switch_text_mode')}
                </button>
                <button type="button" id="lisaMuteToggle" className="lisa-util-link" aria-pressed={muted} onClick={toggleMute}>
                  {muted ? t('lisa.unmute_aria') : t('lisa.mute_aria')}
                </button>
              </div>
              <div className="lisa-transcript" id="lisaTranscript" hidden={!transcriptVisible}>
                {transcript.map((turn, i) => (
                  <div className="lisa-transcript-turn" key={i}>
                    <div className="q">{turn.sender === 'visitor' ? `${t('lisa.you_label')}: ${turn.text}` : ''}</div>
                    {turn.sender === 'lisa' && <div className="a">{turn.text}</div>}
                  </div>
                ))}
              </div>
            </div>
            <a className="lisa-human-cta" id="lisaHumanCta" href={humanCtaHref}>
              {t('lisa.human_expert_cta')} <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
        <div className="lisa-disclosure-banner">{t('lisa.disclosure_banner')}</div>
      </section>
    </>
  );
}
